import { useEffect } from 'react';
import {
  KEYBOARD_MIN_PX,
  centreInScroller,
  focusedField,
  watchKeyboardGeometry,
} from './use-visual-viewport-sheet-height';

/**
 * The gap kept between the sheet's rounded top edge and the top of the visible
 * area — the same 12px the root-level hook trims, so a panel that switches
 * between the two owners does not jump.
 */
export const TOP_GAP_PX = 12;
/**
 * Never cap below this. A sheet shorter than its own pinned footer is unusable,
 * and `visualViewport.height` is not always trustworthy mid-animation (some
 * in-app browsers report a few dozen px while the keyboard slides), so a bogus
 * reading must not be able to collapse the panel.
 */
export const MIN_PANEL_PX = 220;

/**
 * KEEP A BOTTOM SHEET'S PANEL INSIDE THE VISIBLE AREA — height AND top edge.
 *
 * The second half of the sheet contract. `useVisualViewportSheetHeight`
 * publishes `--sheet-max` / `--sheet-lift` on the document ROOT for every sheet
 * at once; this writes the same two properties INLINE on one panel, where they
 * shadow the root ones for that element only (custom properties inherit, and a
 * definition on the element itself wins for its own declarations). It exists for
 * the two things the root pair cannot guarantee by itself:
 *
 * 1. **The cap is stated in px, never in a viewport unit.** A software keyboard
 *    does not shrink `vh` in ANY browser, and does not shrink `dvh` either
 *    unless the engine resizes the layout viewport — so a viewport unit can
 *    quietly be the full screen while the buyer can only see half of it. Worse
 *    on the phone this was measured on: Chrome 101 has no `dvh` AT ALL
 *    (`CSS.supports('height','1dvh')` is false), so a `dvh` anywhere inside
 *    `max-height` makes the declaration invalid at computed-value time and
 *    `max-height` computes to **`none`** — not a loose cap, no cap.
 * 2. **The lift is checked against where the panel actually LANDED.** The lift
 *    is the panel's `margin-bottom` on a `position: fixed; bottom: 0` box, so
 *    `top = layoutHeight − lift − height`: it is only safe while the height is
 *    the capped one. Measured on a real Android phone (Chrome 101, 393×744) on
 *    2026-09-26, on /plans → год → «Оплата по расчётному счёту»: with the
 *    numeric keyboard up the 483px sheet still fitted, and switching to
 *    «Название организации» — text keys plus the suggestion strip — left the
 *    panel 483 tall with its top at **−237**: «модалка слишком вверх
 *    поднимается», header, amount and ИНН all off the screen. So after writing
 *    the two numbers this hook READS the panel's rect back, and if the top is
 *    still above the edge it gives the lift back exactly that much.
 *
 *    ⚠️ Where that −237 came from, corrected 2026-09-26: **NOT the keyboard
 *    inset.** On that build the keyboard shrinks the LAYOUT viewport, so
 *    `innerHeight` was 448 too and no inset was measurable at all — this hook's
 *    own test (`visible < layout − 100`) was false and it had `clear()`ed both
 *    properties. The −237 is `448 − 202 − 483`, where 202 is the ROOT hook's
 *    *assumed* 45% keyboard (it read the 534 → 448 keyboard swap as an 86px
 *    step, too small to be a keyboard, and assumed one on top of the one that
 *    was up) and 483 is an UNCAPPED panel, because every `--sheet-max` value in
 *    the chain carried a `dvh` this browser cannot compute. Two defects, one
 *    number; both are fixed, and case 3 below is why this hook now engages on
 *    that phone at all.
 * 3. **A keyboard that arrives as a LAYOUT resize is still a keyboard.** With
 *    `interactive-widget=resizes-content` Chrome shrinks both viewports
 *    together, so case 1's test can never fire — yet the panel is exactly as
 *    able to overflow the visible area. When `innerHeight` is materially shorter
 *    than the tallest this panel has seen, the cap is written from
 *    `innerHeight` and the lift is written as an explicit **`0px`**: the page is
 *    already short and the sheet is already at the bottom of it, and a literal
 *    `0px` (rather than removing the property) is what SHADOWS a stale root lift
 *    for this one element.
 * 4. **`visualViewport.height` can be a LEFTOVER, and a lift needs a focused
 *    field** (2026-09-26). Inside an iOS host app's WKWebView the keyboard
 *    shrinks the LAYOUT viewport like Android, and when it closes `innerHeight`
 *    comes back while `visualViewport.height` lags and can stop updating for
 *    good. Measured on a paying customer's iPhone 15 Pro (iOS 26.6.1, Instagram's
 *    in-app browser, 393×778, 2026-09-26 15:17:25 UTC): the card modal opened
 *    with the focus on a BUTTON and no keyboard anywhere, `visualViewport.height`
 *    still said ~402 from a keyboard closed minutes earlier, and 778 − 402 = 376
 *    became a lift. So the inset now needs `focusedField()` — no field, no
 *    keyboard, whatever the geometry claims — and a reading that claims one while
 *    nothing is focused, the page is at full height and the visual viewport has
 *    been silent for `VV_STALE_MS` clears the panel outright. See
 *    `watchKeyboardGeometry`, which all three sheet hooks share.
 *
 * ⚠️ When the top and the footer cannot both be saved — an uncapped panel taller
 * than the visible area — the TOP wins. A footer a few px under the keyboard is
 * one scroll away; a header above the screen edge cannot be reached at all, and
 * on this sheet the header is what says which plan and how much.
 *
 * ⚠️ Ownership handoff, on purpose: with no keyboard measurable EITHER WAY both
 * properties are REMOVED from the panel rather than written to a neutral value,
 * so the root hook's own numbers shine through again — including its ASSUMED
 * keyboard, the Instagram/Telegram WebView case where nothing resizes and
 * nothing here can be measured at all.
 *
 * Nothing in here focuses, blurs or `scrollIntoView`s anything: the focused
 * field is brought back into the sheet's own scroller with `centreInScroller`,
 * the one helper that never pans the page out from under a fixed overlay.
 */
export function useSheetPanelViewportCap(
  /** The sheet's scrolling body. Its `[role="dialog"]` ancestor is the panel —
   *  in practice the Radix `DialogContent` — and it is also the element whose
   *  scroll keeps the focused field visible once the cap shortens it. */
  bodyRef: { current: HTMLElement | null },
): void {
  useEffect(() => {
    const vv = typeof window === 'undefined' ? null : window.visualViewport;
    if (!vv) return;

    /*
     * THE TALLEST LAYOUT VIEWPORT SEEN SINCE THIS PANEL MOUNTED, and what this
     * engine does with a keyboard — for cases 3 and 4 above. Shared with the root
     * hook and the page-pad hook, so the three can never disagree about what a
     * keyboard is.
     */
    const layout = watchKeyboardGeometry(vv);

    /** The panel, remembered — the body can unmount before the cleanup runs. */
    let panelEl: HTMLElement | null = null;
    const panelOf = (): HTMLElement | null => {
      const found = bodyRef.current?.closest('[role="dialog"]') ?? null;
      if (found instanceof HTMLElement) panelEl = found;
      return panelEl;
    };

    let lastMax = '';
    let lastLift = '';
    const write = (panel: HTMLElement, max: string, lift: string) => {
      if (max !== lastMax) {
        lastMax = max;
        panel.style.setProperty('--sheet-max', max);
      }
      if (lift !== lastLift) {
        lastLift = lift;
        panel.style.setProperty('--sheet-lift', lift);
      }
    };
    const clear = (panel: HTMLElement | null) => {
      if (lastMax === '' && lastLift === '') return;
      lastMax = '';
      lastLift = '';
      panel?.style.removeProperty('--sheet-max');
      panel?.style.removeProperty('--sheet-lift');
    };

    const apply = () => {
      const panel = panelOf();
      if (!panel) return;
      const layoutH = window.innerHeight;
      layout.note();
      const visible = Math.round(vv.height);
      /*
       * ⚠️ THE READING IS STALE — nothing focused, the page at its full height,
       * and `visualViewport` silent for `VV_STALE_MS` while its height still
       * claims a keyboard (2026-09-26). Clear rather than cap: 390px of panel on a
       * 778px screen with no keyboard is the other half of the customer's
       * screenshot, and a cap is not harmless when it is that wrong.
       */
      if (layout.visualViewportStale()) {
        clear(panel);
        return;
      }
      // A keyboard is HUNDREDS of px; tens are a collapsing URL bar or an
      // accessory strip on its own. Same threshold as the root hook, so the two
      // never disagree about whether a keyboard is up.
      //
      // ⚠️ …and never once this engine has PROVED it resizes the layout viewport
      // for the keyboard: from then on `visualViewport.height` lags `innerHeight`
      // and can stop updating entirely, so the comparison is against a number
      // that is no longer a measurement of anything.
      const visualKeyboard =
        !layout.resizesForKeyboard() && visible > 0 && visible < layoutH - KEYBOARD_MIN_PX;
      /*
       * …AND THE OTHER HALF OF THE SAME QUESTION (2026-09-26). Chrome for
       * Android with `interactive-widget=resizes-content` shrinks the LAYOUT
       * viewport for the keyboard, so both viewports stay equal and the test
       * above is false with the keys plainly up — which is why this hook sat
       * `clear()`ed through the whole of Igor's report. Measured against the
       * tallest layout viewport this panel has seen, 448 < 744 − 100 is the
       * same fact the visual test states on iOS.
       */
      const layoutKeyboard = !visualKeyboard && layout.shrank();
      if (!visualKeyboard && !layoutKeyboard) {
        clear(panel);
        return;
      }
      const cap = Math.max(MIN_PANEL_PX, (visualKeyboard ? visible : layoutH) - TOP_GAP_PX);
      /*
       * The keyboard, LESS any pan the engine already applied (`offsetTop`):
       * that pan moves the fixed overlay up with it, so the sheet needs that
       * much less lift and double-lifting it is itself a way to push the top
       * off the screen. Then clamped so that even at the full cap the panel's
       * top cannot reach the edge.
       *
       * ⚠️ Zero in the layout-resize case, and that is the point: `bottom: 0`
       * is already the top of the keyboard once the page itself is 448px tall,
       * so any lift at all is the −237. It is written as a literal `0px`
       * rather than left unset because the ROOT hook may still be holding a
       * lift of its own, and an inline `0px` on the panel shadows it.
       */
      const inset =
        visualKeyboard && focusedField()
          ? Math.max(0, Math.round(layoutH - vv.height - vv.offsetTop))
          : 0;
      const headroom = Math.max(0, layoutH - cap - TOP_GAP_PX);
      const lift = Math.min(inset, headroom);
      const maxValue = `${cap}px`;
      const liftValue = `${lift}px`;
      /*
       * ⚠️ NOTHING HAPPENS WHEN THE NUMBERS HAVE NOT MOVED, and that guard is
       * load-bearing rather than an optimisation: `visualViewport.scroll` fires
       * on every frame of a pan, and both the rect read below and the scroll
       * further down would then run under the buyer's own finger — the «дёргает»
       * that `centreInScroller` exists to avoid.
       */
      if (maxValue === lastMax && liftValue === lastLift) return;
      write(panel, maxValue, liftValue);
      /*
       * …and now the CHECK, because everything above assumes the cap took hold.
       * On the phone it had not: 483px of panel inside 448px of screen. Give the
       * lift back exactly as much as the top is missing.
       *
       * ⚠️ This never fires in the layout-resize case, because `lift` is 0 there
       * and there is nothing to give back — the cap is the only thing standing
       * between the header and the screen edge, which is why it is written in px.
       */
      const top = Math.round(panel.getBoundingClientRect().top);
      if (top < 0 && lift > 0) {
        const rescued = `${Math.max(0, lift + top)}px`;
        if (rescued !== lastLift) {
          lastLift = rescued;
          panel.style.setProperty('--sheet-lift', rescued);
        }
      }
      /*
       * The panel just moved and the body just got shorter, so the field the
       * buyer is typing in may have gone out of it. Already fully visible →
       * nothing moves (see `centreInScroller`): this runs right after the tap
       * that focused the field, and a field that jumps between two taps is a tap
       * that misses.
       */
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.matches('input, textarea, select')) {
        centreInScroller(active, bodyRef.current);
      }
    };

    /** `apply`, plus the stamp that says the visual viewport is still talking.
     *  ⚠️ Bound only to ITS events — never to a document scroll. */
    const onVvEvent = () => {
      layout.vvReported();
      apply();
    };
    /*
     * THE KEYBOARD SLIDES IN STEPS. Chrome on Android reports the new height
     * over ~300ms, and swapping one keyboard for another (numeric ИНН → text
     * «Название организации», which also brings a suggestion strip) can arrive
     * as a `resize` a frame or two AFTER the `focusin`. So a focus change
     * re-measures a few times and then stops; nothing here depends on a timer
     * landing, it only converges sooner.
     */
    const SETTLE_MS = [120, 320, 560];
    let timers: number[] = [];
    const settle = () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers = SETTLE_MS.map((ms) => window.setTimeout(apply, ms));
    };
    const onFocusChange = () => {
      apply();
      settle();
    };

    apply();
    vv.addEventListener('resize', onVvEvent);
    vv.addEventListener('scroll', onVvEvent);
    document.addEventListener('focusin', onFocusChange);
    document.addEventListener('focusout', onFocusChange);
    return () => {
      vv.removeEventListener('resize', onVvEvent);
      vv.removeEventListener('scroll', onVvEvent);
      document.removeEventListener('focusin', onFocusChange);
      document.removeEventListener('focusout', onFocusChange);
      timers.forEach((id) => window.clearTimeout(id));
      clear(panelEl);
    };
  }, [bodyRef]);
}
